import { Link } from 'react-router-dom';

import type { TodayReport } from '../api/types';

/**
 * Where the day stands, and a way into it.
 *
 * Everything else on the dashboard is built from submissions, so it can only describe work
 * that happened. A visit booked for this morning that nobody opened produces no submission
 * and moves no figure — the estate can miss a third of its visits and the panel reads the
 * same. This section exists to make that countable.
 *
 * Every figure here is a way in rather than a thing to admire. A supervisor reading "3 not
 * started" at four in the afternoon wants the three, so the number itself is the link.
 */

/** The five statuses, in the order the day moves through them. */
const STATUSES = [
  {
    key: 'COMPLETE',
    label: 'Completed',
    of: (visits: TodayReport['visits']) => visits.complete,
    tone: 'success',
  },
  {
    key: 'IN_PROGRESS',
    label: 'In progress',
    of: (visits: TodayReport['visits']) => visits.inProgress,
    tone: 'accent',
  },
  {
    key: 'SCHEDULED',
    label: 'Not started',
    of: (visits: TodayReport['visits']) => visits.scheduled,
    tone: 'muted',
  },
  {
    key: 'FROZEN',
    label: 'Frozen',
    of: (visits: TodayReport['visits']) => visits.frozen,
    tone: 'warning',
  },
  {
    key: 'NOT_PERFORMED',
    label: 'Not visited',
    of: (visits: TodayReport['visits']) => visits.notPerformed,
    tone: 'danger',
  },
] as const;

/**
 * One figure, and where it goes.
 *
 * A count of zero is deliberately *not* a link. The figure still shows — "0 not visited" is
 * good news and worth reading — but clicking it would open a filtered list with nothing in
 * it, and a link that leads nowhere teaches people to stop trusting the ones that don't.
 */
function Stat({
  label,
  value,
  tone,
  to,
  hint,
}: {
  label: string;
  value: number;
  tone: string;
  to?: string;
  hint?: string;
}): JSX.Element {
  const body = (
    <>
      <span className="today-stat-value">{value}</span>
      <span className="today-stat-label">{label}</span>
      {hint !== undefined && <span className="today-stat-hint">{hint}</span>}
    </>
  );

  const className = `today-stat today-stat-${tone}`;

  if (to === undefined || value === 0) {
    return <div className={className}>{body}</div>;
  }

  return (
    <Link className={`${className} today-stat-link`} to={to}>
      {body}
    </Link>
  );
}

export function Today({
  report,
  fieldUserLabel,
}: {
  report: TodayReport;
  fieldUserLabel: string;
}): JSX.Element {
  const { visits, fieldForce } = report;

  return (
    <>
      {/*
        The bar is the reading; the figures below are the detail. Laid out as five numbers
        alone, "4 / 2 / 3 / 0 / 1" has to be added up before it says anything, and the one
        question being asked here — how much of today is left — is a proportion.
      */}
      {visits.total === 0 ? (
        /*
          A quiet day says which way the work is, rather than only that there is none.

          Weekends and holidays are quiet by design, so "nothing booked" on its own reads as
          a fault. Naming the nearest day either side separates the ordinary case — the next
          visits are on Monday — from the one worth acting on, which is a schedule that ran
          out weeks ago and has no next day at all.
        */
        <p className="empty">
          Nothing is booked for {visits.date}.{' '}
          {visits.nextDate !== null ? (
            <>
              The next visits are on <Link to={`/schedule?date=${visits.nextDate}`}>{visits.nextDate}</Link>.
            </>
          ) : visits.previousDate !== null ? (
            <>
              The last were on <Link to={`/schedule?date=${visits.previousDate}`}>{visits.previousDate}</Link>,
              and nothing is booked after today — <Link to="/schedule">generate a schedule</Link>.
            </>
          ) : (
            <>
              No visits have ever been booked.{' '}
              <Link to="/schedule">Generate a schedule</Link> to start.
            </>
          )}
        </p>
      ) : (
        <div
          className="today-bar"
          role="img"
          aria-label={
            `${visits.complete} of ${visits.total} visits completed, ` +
            `${visits.inProgress} in progress, ${visits.scheduled} not started, ` +
            `${visits.frozen} frozen, ${visits.notPerformed} not visited`
          }
        >
          {STATUSES.map((status) => {
            const count = status.of(visits);
            if (count === 0) return null;
            return (
              <span
                key={status.key}
                className={`today-bar-part today-bar-${status.tone}`}
                style={{ width: `${(count / visits.total) * 100}%` }}
              />
            );
          })}
        </div>
      )}

      <div className="today-grid">
        {/*
          Always shown, including on a day when every one of them is zero.

          I hid them at first, on the reasoning that five zeroes are not a breakdown of
          anything. That was wrong for the screen this is: the tiles are the shape of the
          section, and a card that changes what it contains from day to day gives a reader
          nothing to learn. Somebody who knows where "Not visited" sits can check it at a
          glance; somebody who finds it moved or missing has to read the whole card again.
          The sentence above already says the day is empty, so the zeroes confirm rather
          than mislead — and a zero is not a link, so none of them is a dead end.
        */}
        {STATUSES.map((status) => (
          <Stat
            key={status.key}
            label={status.label}
            value={status.of(visits)}
            tone={status.tone}
            to={`/schedule?date=${visits.date}&status=${status.key}`}
          />
        ))}

        {/*
          Named for what it measures. This counts people with a visit booked today, which is
          not the same as people working today — somebody on leave whose schedule was
          generated last month is in this figure, and calling it "on duty today" without
          saying so would be a claim the data cannot support.
        */}
        <Stat
          label="Out today"
          value={visits.fieldUsersOnDuty}
          tone="accent2"
          to={`/schedule?date=${visits.date}`}
          hint={`${fieldUserLabel.toLowerCase()}s with a visit booked`}
        />

        {/*
          Omitted, not zeroed, for a caller who may not be told. A store manager holds
          `report.view` and not `user.view`, and "0 field users" would be a statement about
          the estate rather than about what they are allowed to see.
        */}
        {fieldForce !== null &&
          (fieldForce.schedulableRoles === 0 ? (
            <Stat
              label={`${fieldUserLabel}s`}
              value={0}
              tone="muted"
              hint="no role can be scheduled yet"
            />
          ) : (
            <Stat
              label={`${fieldUserLabel}s`}
              value={fieldForce.active}
              tone="accent3"
              to="/users"
              hint={
                fieldForce.total === fieldForce.active
                  ? 'active'
                  : `active, ${fieldForce.total - fieldForce.active} deactivated`
              }
            />
          ))}
      </div>
    </>
  );
}
